import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, ChevronDown, ChevronUp, Trash2, Eye, MapPin, Star, MailCheck, MailX, RefreshCw, Key, X, VolumeX, Volume2, Fish, Waves, Globe, Calendar } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const UsersManagement = ({ users, loading, error, token, currentUser, onUsersUpdate }) => {
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userReports, setUserReports] = useState({});
  const [userReviewCounts, setUserReviewCounts] = useState({});
  const [userDiveReports, setUserDiveReports] = useState({});
  const [userSpeciesIds, setUserSpeciesIds] = useState({});
  const [passwordChangeUserId, setPasswordChangeUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [roleChangeUserId, setRoleChangeUserId] = useState(null);
  const [changingRole, setChangingRole] = useState(false);
  const [mutedUserIds, setMutedUserIds] = useState([]);

  // Fetch muted users on mount
  useEffect(() => {
    const fetchMutedUsers = async () => {
      try {
        const response = await axios.get(`${API}/admin/muted-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMutedUserIds(response.data.map(m => m.user_id));
      } catch (err) {
        console.error("Failed to fetch muted users:", err);
      }
    };
    if (token) fetchMutedUsers();
  }, [token]);

  const handleMuteUser = async (userId, userName) => {
    const isMuted = mutedUserIds.includes(userId);
    
    if (isMuted) {
      // Unmute
      try {
        await axios.delete(`${API}/admin/users/${userId}/mute`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMutedUserIds(prev => prev.filter(id => id !== userId));
        toast.success(`${userName} has been unmuted`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to unmute user");
      }
    } else {
      // Mute
      const reason = prompt("Enter reason for muting (optional):");
      try {
        await axios.post(
          `${API}/admin/users/${userId}/mute${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMutedUserIds(prev => [...prev, userId]);
        toast.success(`${userName} has been muted`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to mute user");
      }
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    if (!confirm(`Change this user's role to ${newRole.toUpperCase()}?`)) return;
    
    setChangingRole(true);
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role?role=${newRole}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`User role changed to ${newRole}`);
      setRoleChangeUserId(null);
      
      // Refresh users list
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      onUsersUpdate(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change role");
    } finally {
      setChangingRole(false);
    }
  };

  const handleResendVerification = async (userId) => {
    if (!confirm("Resend verification email to this user?")) return;
    
    try {
      await axios.post(
        `${API}/admin/resend-verification/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Verification email sent successfully!");
    } catch (err) {
      console.error("Failed to resend verification:", err);
      toast.error(err.response?.data?.detail || "Failed to send verification email");
    }
  };

  const handleChangePassword = async (userId) => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(
        `${API}/admin/change-user-password/${userId}`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Password changed successfully!");
      setPasswordChangeUserId(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Failed to change password:", err);
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user? This will also delete their dive site reports.")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Notify parent to update users list
      onUsersUpdate(users.filter(u => u.id !== userId));
      toast.success("User deleted successfully");
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleToggleExpand = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
    } else {
      setExpandedUserId(userId);
      
      // Fetch user's dive site reports if not already loaded
      if (!userReports[userId]) {
        try {
          const reportsResponse = await axios.get(`${API}/dive-site-reports/user/${userId}`);
          setUserReports(prev => ({ ...prev, [userId]: reportsResponse.data }));
        } catch (err) {
          console.error("Failed to fetch user site reports:", err);
          setUserReports(prev => ({ ...prev, [userId]: [] }));
        }
      }

      // Fetch user's review count
      if (userReviewCounts[userId] === undefined) {
        try {
          const reviewsResponse = await axios.get(`${API}/reviews/all`);
          const userReviewCount = reviewsResponse.data.filter(r => r.user_id === userId).length;
          setUserReviewCounts(prev => ({ ...prev, [userId]: userReviewCount }));
        } catch (err) {
          console.error("Failed to fetch reviews:", err);
          setUserReviewCounts(prev => ({ ...prev, [userId]: 0 }));
        }
      }

      // Fetch user's dive reports (new system)
      if (userDiveReports[userId] === undefined) {
        try {
          const diveReportsResponse = await axios.get(`${API}/dive-reports/user/${userId}`);
          setUserDiveReports(prev => ({ ...prev, [userId]: diveReportsResponse.data.length }));
        } catch (err) {
          console.error("Failed to fetch dive reports:", err);
          setUserDiveReports(prev => ({ ...prev, [userId]: 0 }));
        }
      }

      // Fetch user's species IDs (identified species from submissions)
      if (userSpeciesIds[userId] === undefined) {
        try {
          const speciesResponse = await axios.get(`${API}/marine-database/user-identifications/${userId}`);
          setUserSpeciesIds(prev => ({ ...prev, [userId]: speciesResponse.data.length }));
        } catch (err) {
          console.error("Failed to fetch species IDs:", err);
          setUserSpeciesIds(prev => ({ ...prev, [userId]: 0 }));
        }
      }
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-700";
      case "pro": return "bg-purple-100 text-purple-700";
      default: return "bg-blue-100 text-blue-700";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">All Users</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center py-8 text-gray-600">Loading users...</p>
        ) : error ? (
          <p className="text-center py-8 text-red-600">{error}</p>
        ) : (
          <div className="space-y-2">
            {/* Desktop Table Header - Hidden on mobile */}
            <div className="hidden lg:grid grid-cols-12 gap-4 p-4 bg-gray-100 font-semibold text-gray-700 rounded-t-lg">
              <div className="col-span-1"></div>
              <div className="col-span-4">User</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1 text-center"></div>
            </div>
            
            {/* User Rows - Responsive layout */}
            {users.map((u) => (
              <div key={u.id} className="border rounded-lg overflow-hidden">
                {/* Main Row - Desktop grid, Mobile stack */}
                <div 
                  className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-4 p-4 hover:bg-gray-50 cursor-pointer lg:items-center"
                  onClick={() => handleToggleExpand(u.id)}
                >
                  {/* Mobile Layout: User info header */}
                  <div className="flex items-center gap-3 lg:hidden">
                    {/* Profile Picture */}
                    {u.profile_pic ? (
                      <img 
                        src={getImageUrl(u.profile_pic)} 
                        alt={u.name} 
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 items-center justify-center flex-shrink-0 ${u.profile_pic ? 'hidden' : 'flex'}`}>
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{u.username && `@${u.username}`}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getRoleBadgeColor(u.role)}`}>
                          {u.role.toUpperCase()}
                        </span>
                        {u.email_verified ? (
                          <MailCheck className="w-3 h-3 text-green-600" />
                        ) : (
                          <MailX className="w-3 h-3 text-yellow-600" />
                        )}
                      </div>
                      <div className="text-sm text-gray-600 truncate">{u.name}</div>
                      <div className="text-xs text-gray-500 truncate">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {u.id !== currentUser.id && u.role !== 'admin' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMuteUser(u.id, u.name); }}
                          className={`p-1 ${mutedUserIds.includes(u.id) ? 'text-green-500' : 'text-orange-500'}`}
                        >
                          {mutedUserIds.includes(u.id) ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                        </button>
                      )}
                      {u.id !== currentUser.id && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.id); }} className="p-1 text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {expandedUserId === u.id ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                    </div>
                  </div>
                  
                  {/* Desktop Layout: Grid columns */}
                  {/* Profile Picture - Desktop */}
                  <div className="hidden lg:block col-span-1">
                    {u.profile_pic ? (
                      <img 
                        src={getImageUrl(u.profile_pic)} 
                        alt={u.name} 
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 items-center justify-center ${u.profile_pic ? 'hidden' : 'flex'}`}>
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  {/* Username & Name - Desktop */}
                  <div className="hidden lg:block col-span-4">
                    <div className="font-semibold text-gray-900">
                      {u.username && `@${u.username}`}
                    </div>
                    <div className="text-sm text-gray-600">{u.name}</div>
                  </div>
                  
                  {/* Role - Desktop */}
                  <div className="hidden lg:block col-span-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(u.role)}`}>
                      {u.role.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Email - Desktop */}
                  <div className="hidden lg:block col-span-3 text-sm text-gray-600">
                    {u.email}
                  </div>
                  
                  {/* Email Verification Status - Desktop */}
                  <div className="hidden lg:block col-span-1">
                    {u.email_verified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700" title="Email verified">
                        <MailCheck className="w-3 h-3" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700" title="Email not verified">
                        <MailX className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  
                  {/* Actions - Desktop */}
                  <div className="hidden lg:flex col-span-1 items-center justify-center gap-2">
                    {expandedUserId === u.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                    {u.id !== currentUser.id && u.role !== 'admin' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMuteUser(u.id, u.name);
                        }}
                        className={`${mutedUserIds.includes(u.id) ? 'text-green-500 hover:text-green-700' : 'text-orange-500 hover:text-orange-700'}`}
                        title={mutedUserIds.includes(u.id) ? 'Unmute user' : 'Mute user'}
                      >
                        {mutedUserIds.includes(u.id) ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                      </button>
                    )}
                    {u.id !== currentUser.id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUser(u.id);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedUserId === u.id && (
                  <div className="p-6 bg-gray-50 border-t">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Profile Details */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Profile Details</h4>
                        
                        <div>
                          <label className="text-xs text-gray-500">Email Verification</label>
                          <div className="flex items-center gap-2 mt-1">
                            {u.email_verified ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                <MailCheck className="w-4 h-4" />
                                Verified
                              </span>
                            ) : (
                              <>
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                                  <MailX className="w-4 h-4" />
                                  Not Verified
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-cyan-600 border-cyan-600 hover:bg-cyan-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResendVerification(u.id);
                                  }}
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Resend
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-xs text-gray-500">Country</label>
                          <p className="font-medium">{u.country || "Not specified"}</p>
                        </div>
                        
                        <div>
                          <label className="text-xs text-gray-500">Diving Level</label>
                          <p className="font-medium">{u.diving_level || "Not specified"}</p>
                        </div>
                        
                        <div>
                          <label className="text-xs text-gray-500">Age</label>
                          <p className="font-medium">{u.age || "Not specified"}</p>
                        </div>
                        
                        <div>
                          <label className="text-xs text-gray-500">Password</label>
                          {passwordChangeUserId === u.id ? (
                            <div className="mt-2 space-y-3 p-3 bg-white rounded-lg border">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-2">
                                  <Key className="w-4 h-4 text-cyan-600" />
                                  Change Password
                                </span>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPasswordChangeUserId(null);
                                    setNewPassword("");
                                    setConfirmPassword("");
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="space-y-2">
                                <div>
                                  <Label className="text-xs">New Password</Label>
                                  <Input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Min 6 characters"
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Confirm Password</Label>
                                  <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Repeat password"
                                    className="h-8 text-sm"
                                  />
                                </div>
                              </div>
                              <Button
                                size="sm"
                                disabled={changingPassword || !newPassword || !confirmPassword}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangePassword(u.id);
                                }}
                                className="w-full bg-cyan-500 hover:bg-cyan-600"
                              >
                                {changingPassword ? "Saving..." : "Update Password"}
                              </Button>
                            </div>
                          ) : (
                            <>
                              <p className="font-mono text-sm">••••••••</p>
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPasswordChangeUserId(u.id);
                                }}
                              >
                                <Key className="w-3 h-3 mr-1" />
                                Change Password
                              </Button>
                            </>
                          )}
                        </div>
                        
                        {/* Role Change */}
                        {u.id !== currentUser.id && (
                          <div>
                            <label className="text-xs text-gray-500">Change Role</label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {["member", "pro", "admin"].map((role) => (
                                <Button
                                  key={role}
                                  size="sm"
                                  variant={u.role === role ? "default" : "outline"}
                                  className={u.role === role ? "bg-cyan-600" : ""}
                                  disabled={u.role === role || changingRole}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeRole(u.id, role);
                                  }}
                                >
                                  {role.toUpperCase()}
                                </Button>
                              ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              Current: {u.role.toUpperCase()}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Activity Stats */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Activity</h4>
                        
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                          <div className="flex items-center gap-3">
                            <Waves className="w-5 h-5 text-blue-600" />
                            <span className="text-gray-700">Dive Reports</span>
                          </div>
                          <span className="font-bold text-blue-600">
                            {userDiveReports[u.id] ?? "..."}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                          <div className="flex items-center gap-3">
                            <Fish className="w-5 h-5 text-green-600" />
                            <span className="text-gray-700">Species IDs</span>
                          </div>
                          <span className="font-bold text-green-600">
                            {userSpeciesIds[u.id] ?? "..."}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                          <div className="flex items-center gap-3">
                            <Star className="w-5 h-5 text-yellow-600" />
                            <span className="text-gray-700">Reviews</span>
                          </div>
                          <span className="font-bold text-yellow-600">
                            {userReviewCounts[u.id] ?? "..."}
                          </span>
                        </div>

                        {/* Additional Profile Info */}
                        {u.currently_in && (
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <div className="flex items-center gap-3">
                              <Globe className="w-5 h-5 text-purple-600" />
                              <span className="text-gray-700">Currently In</span>
                            </div>
                            <span className="font-medium text-purple-600">
                              {u.currently_in}
                            </span>
                          </div>
                        )}
                        
                        {u.created_at && (
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <div className="flex items-center gap-3">
                              <Calendar className="w-5 h-5 text-gray-500" />
                              <span className="text-gray-700">Joined</span>
                            </div>
                            <span className="font-medium text-gray-600">
                              {new Date(u.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`/profile/${u.id}`, '_blank');
                            }}
                            className="flex-1"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Profile
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsersManagement;
